import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useParams } from 'react-router-dom';
import Menu from '@/components/Menu';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import { useAutofillFromResume } from '@/hooks/useAutofillFromResume';
import candidateEducation, {
  NewEducationType,
  DeleteEducationType,
} from '@/services/candidate-education';
import TransparentButton from '@/trio-ui/TransparentButton';
import CandidateEducationComponent from './CandidateEducation.component';
import { EducationType } from '../CandidateEducationItem/CandidateEducationItem.types';
import DialogBox from '../DialogBox';
import SectionBox from '../SectionBox';

type CandidateEducationContainerProps = {
  defaultEducationItems: EducationType[];
};

const educationFields = {
  university: '',
  degree: '',
  endMonth: '',
  endYear: '',
  startMonth: '',
  startYear: '',
  currentlyPursuing: false,
};

const defaultCandidateEducation = {
  ...educationFields,
  isEditing: true,
};

type CandidateEducationType = Omit<typeof defaultCandidateEducation, 'isEditing'> & {
  isEditing?: boolean;
  id?: string;
};

const CandidateEducation = ({
  defaultEducationItems,
}: CandidateEducationContainerProps): JSX.Element => {
  const [educations, setEducations] = useState<CandidateEducationType[]>(defaultEducationItems);

  const [educationIndexToDelete, setEducationsIndexToDelete] = useState('');

  const toast = useToastContext();
  const { id } = useParams();
  const candidateId = id ?? '';

  const { extractFromResume, isLoading } = useAutofillFromResume(candidateId as string);

  const createNewProject = useApiSubmit<any, NewEducationType>(
    candidateEducation.createNewEducation
  );
  const updateProject = useApiSubmit<any, NewEducationType>(candidateEducation.updateEducation);
  const deleteProjectMutation = useApiSubmit<any, DeleteEducationType>(
    candidateEducation.deleteEducation
  );

  useEffect(() => {
    if (!educations.length) {
      setEducations([defaultCandidateEducation]);
    }
  }, [educations]);

  const onAddNewEducation = (): void => {
    setEducations((prev) => prev.concat(defaultCandidateEducation));
  };

  const onDeleteEducation = async (index: number): Promise<void> => {
    try {
      const educationIdToDelete = educations[index]?.id ?? '';
      if (educationIdToDelete) {
        await deleteProjectMutation.mutateAsync({
          id: educationIdToDelete,
          candidateId,
        });
        toast.showSuccessFeedback('The project has been deleted.');
      }

      setEducations((prev) => prev.filter((p, i) => i !== index));
      setEducationsIndexToDelete('');

      toast.showSuccessFeedback('The education has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the education.');
    }
  };

  const onEditEducation = (index: number): void => {
    setEducations((prev) =>
      prev.map((education, i) => {
        if (i !== index) {
          return education;
        }
        return {
          ...education,
          isEditing: true,
        };
      })
    );
  };

  const onSaveEducation = async (values: any, index: number): Promise<void> => {
    try {
      const isEditing = !!educations[index].id;
      const { endYear, endMonth, startYear, startMonth, currentlyPursuing, ...rest } = values;
      const methodToCall = isEditing ? updateProject : createNewProject;
      const payload = {
        ...rest,
        start: {
          year: startYear,
          month: startMonth,
        },
        end: {
          year: currentlyPursuing ? '' : endYear,
          month: currentlyPursuing ? '' : endMonth,
        },
      };

      const newEducation = await methodToCall.mutateAsync({ ...payload, candidateId });

      setEducations((prev) =>
        prev.map((education, i) => {
          if (i !== index) {
            return education;
          }

          return {
            ...education,
            ...values,
            id: newEducation.id,
            isEditing: false,
          };
        })
      );
      toast.showSuccessFeedback('The education has been saved.');
    } catch {
      toast.showErrorFeedback('There was a problem to save the education.');
    }
  };

  const onCancelEducationEdition = (index: number): void => {
    const currentEducation = educations[index];

    if (currentEducation?.id) {
      setEducations((prev) =>
        prev.map((education, i) => {
          if (i !== index) {
            return education;
          }

          return {
            ...education,
            isEditing: false,
          };
        })
      );
    } else {
      setEducations((prev) => prev.filter((p, i) => i !== index));
    }
  };

  const updateIndexToDelete = (index: number): void => {
    setEducationsIndexToDelete(index.toString());
  };

  const onAutofillFromResume = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();

    try {
      const education = await extractFromResume('education');

      const educationList = typeof education === 'string' ? JSON.parse(education) : education;

      if (!Array.isArray(educationList)) {
        throw new Error('Education data is not in the expected format');
      }

      const formattedEducations = educationList.map((currentEducation) => ({
        ...currentEducation,
        endMonth:
          currentEducation.endMonth === 'Not provided' ? 'Month' : currentEducation.endMonth,
        endYear: currentEducation.endYear === 'Not provided' ? 'Year' : currentEducation.endYear,
        startMonth:
          currentEducation.startMonth === 'Not provided' ? 'Month' : currentEducation.startMonth,
        startYear:
          currentEducation.startYear === 'Not provided' ? 'Year' : currentEducation.startYear,
      }));

      await Promise.all(
        formattedEducations.map(async (currentEducation) => {
          const { endYear, endMonth, startYear, startMonth, currentlyPursuing, ...rest } =
            currentEducation;
          const payload = {
            ...rest,
            start: {
              year: startYear,
              month: startMonth,
            },
            end: {
              year: currentlyPursuing ? '' : endYear,
              month: currentlyPursuing ? '' : endMonth,
            },
          };

          const newEducation = await createNewProject.mutateAsync({ ...payload, candidateId });

          return newEducation;
        })
      );

      setEducations((prev) => [...prev, ...formattedEducations]);

      toast.showSuccessFeedback('Experiences have been imported from resume.');
    } catch (error) {
      toast.showErrorFeedback(
        'Failed to import education from resume. Please check if the resume is a valid PDF file.'
      );
    }
  };

  const normalizedEducations = useMemo(() => {
    return educations.map((education) => ({
      ...education,
      endMonth: education.endMonth || 'Month',
      endYear: education.endYear || 'Year',
      startMonth: education.startMonth || 'Month',
      startYear: education.startYear || 'Year',
    }));
  }, [educations]);

  return (
    <>
      <SectionBox
        id="education"
        title="Education"
        leftElement={
          isLoading ? (
            <Spinner size="xs" />
          ) : (
            <TransparentButton style={{ height: '24px' }} isBlueButton onClick={onAddNewEducation}>
              + Add education
            </TransparentButton>
          )
        }
        rightElement={
          <Menu
            icon={MoreHorizIcon}
            menuId="autofill-education-from-resume-button"
            menuItems={[
              {
                label: 'Autofill from Resume',
                menuItemIcon: AutoFixHigh,
                callback: onAutofillFromResume,
              },
            ]}
          />
        }
      >
        <>
          {normalizedEducations.map((education, index) => {
            return (
              <CandidateEducationComponent
                {...education}
                key={`${education.id ?? index.toString()}}`}
                onDelete={() => updateIndexToDelete(index)}
                onEditInfo={() => onEditEducation(index)}
                onSaveEducation={(values) => onSaveEducation(values, index)}
                onCancelEducationEdition={() => onCancelEducationEdition(index)}
                isEditing={education.isEditing as boolean}
              />
            );
          })}
          <TransparentButton
            style={{ height: '24px', marginTop: '30px' }}
            isBlueButton
            onClick={onAddNewEducation}
          >
            + Add education
          </TransparentButton>
        </>
      </SectionBox>

      <DialogBox
        isOpen={!!educationIndexToDelete}
        onCancelHandler={() => setEducationsIndexToDelete('')}
        onConfirmHandler={() => onDeleteEducation(Number(educationIndexToDelete))}
      />
    </>
  );
};

export default CandidateEducation;
